"use client";

import { useState, useEffect } from "react";
import { ArrowUpRight, Upload } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
type GalleryItem = {
  src: string;
  srcset: string;
  sizes: string;
  alt: string;
  event: string;
  year: string;
  colSpan: string;
  aspect: string;
  srcClassName?: string;
};
type UploadedImage = {
  url: string;
  alt: string;
};
const galleryItems: GalleryItem[] = [{
  src: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/7bdd9085-4b0c-4668-ab50-40c8942c4836.jpg?v=zxzn4v6jcx",
  srcset: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/7bdd9085-4b0c-4668-ab50-40c8942c4836.jpg?v=wxm7k2lglnp",
  sizes: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/7bdd9085-4b0c-4668-ab50-40c8942c4836.jpg?v=mfy3fx2pl",
  alt: "Precision concrete foundation pour at a residential site",
  event: "RESIDENTIAL FOUNDATION",
  year: "MMXXIV",
  colSpan: "lg:col-span-7",
  aspect: "aspect-[4/5]",
  srcClassName: "absolute inset-0 z-20 bg-background transition-transform duration-700 ease-out translate-y-full !bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719873/img/5bca95d8-a920-4fd3-9198-3b23a581abd7-1.webp)]"
}, {
  src: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/e33065ba-02b1-41b1-b21b-47ae2333c09c.jpg?v=kc0oyeifiql",
  srcset: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/e33065ba-02b1-41b1-b21b-47ae2333c09c.jpg?v=b8btbo5baw",
  sizes: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/e33065ba-02b1-41b1-b21b-47ae2333c09c.jpg?v=jkannkxl5c9",
  alt: "Detail of high-quality concrete finishing",
  event: "FINISH QUALITY",
  year: "MMXXIII",
  colSpan: "lg:col-span-3 lg:col-start-10",
  aspect: "aspect-square"
}, {
  src: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/14806a64-cb5c-49c0-8362-a3c7d6604acf.jpg?v=6mmlndp5pba",
  srcset: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/14806a64-cb5c-49c0-8362-a3c7d6604acf.jpg?v=19azqqk8927h",
  sizes: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/14806a64-cb5c-49c0-8362-a3c7d6604acf.jpg?v=bd4m4ozc8e",
  alt: "Skilled labor team working on site",
  event: "SKILLED WORKFORCE",
  year: "MMXXIV",
  colSpan: "lg:col-span-7",
  aspect: "aspect-[4/5]"
}, {
  src: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/e3080571-e416-4b8f-b3d8-c3079849f019.jpg?v=1zjs4u0lwzn",
  srcset: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/e3080571-e416-4b8f-b3d8-c3079849f019.jpg?v=oj6pbpx0ssb",
  sizes: "https://genuine-manatee.10web.cloud/wp-content/uploads/2026/06/e3080571-e416-4b8f-b3d8-c3079849f019.jpg?v=0fad7o3t9ltl",
  alt: "Overview of a construction site in Vancouver",
  event: "VANCOUVER OPERATIONS",
  year: "MMXXII",
  colSpan: "lg:col-span-4 lg:col-start-9",
  aspect: "aspect-[3/4]"
}];
function RevealImage({
  item,
  index
}: {
  item: GalleryItem;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -50px 0px"
  });
  return <div ref={ref} data-index={index} className={`${item.colSpan} relative overflow-hidden group transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className={`${item.aspect} relative overflow-hidden bg-muted`}>
        <img src={item.src} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6">
          <p className="text-white font-mono text-[10px] tracking-widest uppercase mb-1">{item.year}</p>
          <h3 className="text-white font-bold text-xl leading-tight">{item.event}</h3>
        </div>
      </div>
    </div>;
}
function RevealPlaceholder({
  index
}: {
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    rootMargin: "0px 0px -50px 0px"
  });
  return <div ref={ref} data-index={index} className={`relative overflow-hidden group transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="aspect-square bg-muted flex items-center justify-center relative overflow-hidden">
        <div className="flex flex-col items-center gap-2 transition-transform duration-700 group-hover:scale-110">
          
          
        </div>
      </div>
    </div>;
}
export default function Portfolio() {
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  useEffect(() => {
    if (typeof window !== 'undefined' && window.wp && window.wp.media) {
      const mediaUploader = (window as any).wp.media({
        title: 'Select Images',
        button: {
          text: 'Use these images'
        },
        multiple: true
      });
      mediaUploader.on('select', () => {
        const attachments = mediaUploader.state().get('selection').toJSON();
        const newImages = attachments.map((img: any) => ({
          url: img.url,
          alt: img.alt || 'Uploaded project image'
        }));
        setUploadedImages(prev => [...prev, ...newImages]);
      });
      (window as any).mediaUploader = mediaUploader;
    }
  }, []);
  const handleUploadClick = () => {
    if ((window as any).mediaUploader) {
      (window as any).mediaUploader.open();
    }
  };
  const placeholders = Array.from({
    length: 7
  });
  return <section data-section-id="3732" id="portafolio-de-proyectos" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32">
      <div className="px-6 md:px-12">
        <div ref={headerRef} className={`grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8 mb-16 md:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7 lg:col-start-1">
            <h1 className="font-default font-bold leading-[0.95] text-foreground text-[44px] sm:text-[56px] md:text-[64px] lg:text-[64px] tracking-tight">PROJECTS ON GOING</h1>
          </div>
        </div>

        

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-4">
          {galleryItems.map((item, i) => <RevealImage key={i} item={item} index={i} />)}
        </div>

        
      </div>
    </section>;
}